import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { authConfigured } from '../auth/config';
import AuthPanel from '../components/AuthPanel';
import { Button } from '../ui/button';
import { ErrorMessage, Field, Label } from '../ui/fieldset';
import { Heading } from '../ui/heading';
import { Input } from '../ui/input';
import { Text } from '../ui/text';

interface LocationState {
  from?: string;
}

export default function Login() {
  const { signIn, completeNewPassword, challenge } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from =
    searchParams.get('from') || (location.state as LocationState | null)?.from || '/';
  const signupHref =
    from && from !== '/' ? `/signup?from=${encodeURIComponent(from)}` : '/signup';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  if (!authConfigured) {
    return (
      <div className="mx-auto max-w-md py-10 text-center">
        <Heading level={2}>Sign in disabled</Heading>
        <Text className="mt-2">
          Auth is not configured in this build. Set <code>VITE_USER_POOL_ID</code> and
          <code> VITE_USER_POOL_CLIENT_ID</code> in <code>web/.env.production</code>.
        </Text>
      </div>
    );
  }

  const submitSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const res = await signIn(email, password);
      if (!res.challenge) navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      await completeNewPassword(newPassword);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (challenge === 'NEW_PASSWORD_REQUIRED') {
    return (
      <AuthPanel
        eyebrow="Account setup"
        title="Set a new password"
        body="Finish the first sign-in step before entering the admin or fan area."
      >
        <form onSubmit={submitNewPassword} className="max-w-sm space-y-5">
          <Field>
            <Label>New password</Label>
            <Input
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Text className="mt-1 text-xs">10+ chars, upper/lower/number.</Text>
          </Field>
          {error ? <ErrorMessage>{error}</ErrorMessage> : null}
          <Button type="submit" disabled={busy} color="dark/zinc" className="w-full">
            {busy ? 'Saving…' : 'Save password'}
          </Button>
        </form>
      </AuthPanel>
    );
  }

  return (
    <AuthPanel
      eyebrow="Welcome back"
      title="Sign in to BaseballSTRM"
      body="Use your team admin, scorekeeper, or approved fan account to manage live games and watch private streams."
    >
      <form onSubmit={submitSignIn} className="max-w-sm space-y-5">
        <Field>
          <Label>Email</Label>
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field>
          <Label>Password</Label>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error ? <ErrorMessage>{error}</ErrorMessage> : null}
        <Button type="submit" disabled={busy} color="dark/zinc" className="w-full">
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        <Text className="text-center text-sm">
          New here?{' '}
          <Link to={signupHref} className="font-medium text-zinc-200 hover:underline">
            Create an account
          </Link>
        </Text>
      </form>
    </AuthPanel>
  );
}
